import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography, Chip, Alert, CircularProgress,
  Grid, IconButton, Tooltip, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions, LinearProgress
} from '@mui/material';
import {
  Refresh, LibraryBooks, Search as ScanIcon, DeleteSweep, PhotoLibrary,
  Folder, Image as ImageIcon, VideoLibrary, Storage
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { GlassmorphicCard, BodyText, SecondaryText } from '../styles/sharedStyles';

const StyledCard = styled(GlassmorphicCard)(({ theme }) => ({
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: `${(theme.custom?.cardRounding || 28) * 0.3}px`,
  fontSize: '0.75rem',
  padding: '4px 12px',
  minWidth: 'unset',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
}));

const ImmichLibraries = ({ apiBaseUrl, token }) => {
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanningLibrary, setScanningLibrary] = useState(null);
  const [removingOffline, setRemovingOffline] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, libraryId: null, libraryName: '', action: '' });

  const fetchLibraries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiBaseUrl}/api/immich/libraries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLibraries(response.data || []);
    } catch (err) {
      console.error('Error fetching Immich libraries:', err);
      setError(err.response?.data?.detail || 'Failed to fetch Immich libraries');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchLibraries();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchLibraries, 30000);
    return () => clearInterval(interval);
  }, [fetchLibraries]);

  const handleScanLibrary = async (libraryId) => {
    setScanningLibrary(libraryId);
    setError(null);
    setSuccessMessage(null);
    try {
      await axios.post(`${apiBaseUrl}/api/immich/libraries/${libraryId}/scan`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMessage('Library scan triggered successfully');
      setTimeout(fetchLibraries, 2000);
    } catch (err) {
      console.error('Error scanning library:', err);
      setError(err.response?.data?.detail || 'Failed to scan library');
    } finally {
      setScanningLibrary(null);
      setConfirmDialog({ open: false, libraryId: null, libraryName: '', action: '' });
    }
  };

  const handleRemoveOffline = async (libraryId) => {
    setRemovingOffline(libraryId);
    setError(null);
    setSuccessMessage(null);
    try {
      await axios.post(`${apiBaseUrl}/api/immich/libraries/${libraryId}/remove-offline`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMessage('Remove offline files triggered successfully');
      setTimeout(fetchLibraries, 2000);
    } catch (err) {
      console.error('Error removing offline files:', err);
      setError(err.response?.data?.detail || 'Failed to remove offline files');
    } finally {
      setRemovingOffline(null);
      setConfirmDialog({ open: false, libraryId: null, libraryName: '', action: '' });
    }
  };

  const openConfirmDialog = (libraryId, libraryName, action) => {
    setConfirmDialog({ open: true, libraryId, libraryName, action });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, libraryId: null, libraryName: '', action: '' });
  };

  const formatBytes = (bytes) => {
    if (bytes === 0 || bytes === undefined || bytes === null) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getLibraryTypeIcon = (type) => {
    switch (type) {
      case 'UPLOAD':
        return <Storage sx={{ color: '#00d4ff', fontSize: 28 }} />;
      case 'EXTERNAL':
        return <Folder sx={{ color: '#ffc107', fontSize: 28 }} />;
      default:
        return <LibraryBooks sx={{ color: '#00d4ff', fontSize: 28 }} />;
    }
  };

  if (loading && libraries.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
          Immich Libraries ({libraries.length})
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchLibraries} disabled={loading} sx={{ color: '#00d4ff' }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {libraries.length === 0 ? (
        <Box sx={{
          textAlign: 'center',
          py: 6,
          color: 'rgba(255, 255, 255, 0.7)'
        }}>
          <Typography variant="body1">
            {loading ? 'Loading libraries...' : 'No libraries found'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {libraries.map((library) => (
            <Grid item xs={12} md={6} lg={4} key={library.id}>
              <StyledCard>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getLibraryTypeIcon(library.type)}
                      <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1rem' }}>
                        {library.name || 'Unnamed Library'}
                      </Typography>
                    </Box>
                    <Chip
                      label={library.type || 'Unknown'}
                      size="small"
                      sx={{
                        background: library.type === 'UPLOAD' ? 'rgba(0, 212, 255, 0.1)' : 'rgba(255, 193, 7, 0.1)',
                        color: library.type === 'UPLOAD' ? '#00d4ff' : '#ffc107',
                        border: `1px solid ${library.type === 'UPLOAD' ? 'rgba(0, 212, 255, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`,
                      }}
                    />
                  </Box>

                  {library.importPaths && library.importPaths.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <SecondaryText sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                        Import Paths
                      </SecondaryText>
                      {library.importPaths.map((path, idx) => (
                        <BodyText key={idx} sx={{ fontSize: '0.875rem', wordBreak: 'break-all' }}>
                          {path}
                        </BodyText>
                      ))}
                    </Box>
                  )}

                  {library.statistics && (
                    <Box sx={{ 
                      mb: 2, 
                      p: 1.5, 
                      backgroundColor: (theme) => `${theme.palette.primary.main}0d`,
                      borderRadius: (theme) => `${(theme.custom?.cardRounding || 28) * 0.3}px`,
                      border: (theme) => `1px solid ${theme.palette.primary.main}1a`
                    }}>
                      <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500, mb: 1 }}>
                        Statistics
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {library.statistics.photos !== undefined && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ImageIcon sx={{ color: '#00d4ff', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              {library.statistics.photos} photos
                            </Typography>
                          </Box>
                        )}
                        {library.statistics.videos !== undefined && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <VideoLibrary sx={{ color: '#ff4081', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              {library.statistics.videos} videos
                            </Typography>
                          </Box>
                        )}
                        {library.statistics.total !== undefined && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhotoLibrary sx={{ color: '#00ff88', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              {library.statistics.total} total
                            </Typography>
                          </Box>
                        )}
                        {library.statistics.usage !== undefined && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Storage sx={{ color: '#ffc107', fontSize: 16 }} />
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              {formatBytes(library.statistics.usage)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <ActionButton
                      variant="contained"
                      size="small"
                      startIcon={scanningLibrary === library.id ? <CircularProgress size={14} sx={{ color: '#ffffff' }} /> : <ScanIcon />}
                      onClick={() => openConfirmDialog(library.id, library.name, 'scan')}
                      disabled={scanningLibrary === library.id}
                      sx={{
                        backgroundColor: (theme) => theme.palette.secondary.main,
                        color: (theme) => theme.palette.secondary.contrastText,
                        flex: 1,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.secondary.main,
                          filter: 'brightness(1.1)'
                        },
                      }}
                    >
                      {scanningLibrary === library.id ? 'Scanning...' : 'Scan'}
                    </ActionButton>
                    <ActionButton
                      variant="outlined"
                      size="small"
                      startIcon={removingOffline === library.id ? <CircularProgress size={14} sx={{ color: '#ff4444' }} /> : <DeleteSweep />}
                      onClick={() => openConfirmDialog(library.id, library.name, 'remove')}
                      disabled={removingOffline === library.id}
                      sx={{
                        borderColor: 'rgba(255, 68, 68, 0.5)',
                        color: '#ff8888',
                        flex: 1,
                        '&:hover': {
                          borderColor: '#ff4444',
                          background: 'rgba(255, 68, 68, 0.1)',
                        },
                      }}
                    >
                      {removingOffline === library.id ? 'Removing...' : 'Clean Offline'}
                    </ActionButton>
                  </Box>

                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <SecondaryText sx={{ fontSize: '0.7rem' }}>
                      ID: {library.id}
                    </SecondaryText>
                    {library.createdAt && (
                      <SecondaryText sx={{ fontSize: '0.7rem' }}>
                        Created: {new Date(library.createdAt).toLocaleDateString()}
                      </SecondaryText>
                    )}
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        PaperProps={{
          sx: {
            background: (theme) => theme.palette.background.paper,
            backdropFilter: (theme) => `blur(${theme.custom?.glassBlur || 20}px)`,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: (theme) => `${theme.custom?.cardRounding || 28}px`,
          }
        }}
      >
        <DialogTitle sx={{ color: '#ffffff' }}>
          Confirm {confirmDialog.action === 'scan' ? 'Library Scan' : 'Remove Offline Files'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Are you sure you want to {confirmDialog.action === 'scan' ? 'scan' : 'remove offline files from'} the library "{confirmDialog.libraryName}"?
          </Typography>
          {confirmDialog.action === 'remove' && (
            <Alert severity="warning" sx={{ mt: 2, backgroundColor: 'rgba(255, 193, 7, 0.1)', color: '#ffc107' }}>
              This will remove all files that are no longer accessible from the library.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={closeConfirmDialog}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { color: '#ffffff' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.action === 'scan') {
                handleScanLibrary(confirmDialog.libraryId);
              } else {
                handleRemoveOffline(confirmDialog.libraryId);
              }
            }}
            variant="contained"
            sx={{
              backgroundColor: (theme) => confirmDialog.action === 'scan' 
                ? theme.palette.secondary.main
                : theme.palette.error.main,
              color: (theme) => confirmDialog.action === 'scan' 
                ? theme.palette.secondary.contrastText
                : theme.palette.error.contrastText,
              '&:hover': {
                backgroundColor: (theme) => confirmDialog.action === 'scan'
                  ? theme.palette.secondary.main
                  : theme.palette.error.main,
                filter: 'brightness(1.1)'
              }
            }}
          >
            {confirmDialog.action === 'scan' ? 'Scan' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImmichLibraries;
